import { createSelector } from 'reselect';
import { initialState } from '../reducers/crmObjectEditingReducer';
import get from 'hs-lodash/get';
import { getIsLandingPage, getIsBlogPost, getIsEmail, getIsSitePage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
// Basic selector to get the entire CRM object editing state
export const getCrmObjectEditing = createSelector([state => state.crmObjectEditing], crmObjectEditing => crmObjectEditing || initialState);
export const getCrmObjectSchemaAsCmv2Schema = createSelector([getCrmObjectEditing], crmObjectEditing => crmObjectEditing.fieldSchema);

// Selector to get the CRM objects to edit
export const getCrmObjectsToEdit = createSelector([getCrmObjectEditing], crmObjectEditing => crmObjectEditing.crmObjectByKey);

// Need to convert CRM object shape to basic format
// const fieldValues = {
//   quote: {
//     properties: {
//       hs_cover_letter: '<h1>foobar</h1>',
//     },
//   },
// };
export const getCrmObjectValuesAsFieldValues = createSelector([getCrmObjectSchemaAsCmv2Schema, getCrmObjectsToEdit], (crmObjectSchemaAsCmv2Schema, crmObjectsToEdit) => {
  if (!crmObjectSchemaAsCmv2Schema || !crmObjectsToEdit) {
    return {};
  }
  const result = {};

  // Helper function to traverse the schema and build result object
  const traverseCmv2Schema = (schema, parentPath = [], targetObj = result, objectsToEdit = crmObjectsToEdit) => {
    schema.forEach(field => {
      const currentPath = [...parentPath, field.name];
      if (field.type === 'group' && field.children) {
        // For group type, create a nested object and process children
        const groupObj = {};
        targetObj[field.name] = groupObj;
        traverseCmv2Schema(field.children, currentPath, groupObj, objectsToEdit);
      } else {
        // For leaf fields, extract the value from objectsToEdit using lodash get
        const value = get(objectsToEdit, currentPath);

        // Add .value to access the actual value
        if (value !== undefined && value !== null && 'value' in value) {
          targetObj[field.name] = value.value;
        }
      }
    });
  };

  // Start traversal with the root schema
  Object.entries(crmObjectSchemaAsCmv2Schema).forEach(([key, schema]) => {
    if (Array.isArray(schema) && !Array.isArray(crmObjectsToEdit[key])) {
      result[key] = {};
      traverseCmv2Schema(schema, [key], result[key], crmObjectsToEdit);
    } else if (Array.isArray(schema) && Array.isArray(crmObjectsToEdit[key])) {
      const objectsArray = crmObjectsToEdit[key];
      objectsArray.forEach((objectInstance, index) => {
        result[key] = result[key] || [];
        result[key][index] = {};
        traverseCmv2Schema(schema, [], result[key][index], objectInstance);
      });
    }
  });
  return result;
});

// Selector to get the modified paths waiting to save
export const getModifiedPathsWaitingToSave = createSelector([getCrmObjectEditing], crmObjectEditing => crmObjectEditing.modifiedPathsWaitingToSave);

// Selector to check if there are any modified paths waiting to save
export const getHasModifiedPathsWaitingToSave = createSelector([getModifiedPathsWaitingToSave], modifiedPaths => modifiedPaths.length > 0);

// Selector to get the count of modified paths waiting to save
export const getModifiedPathsWaitingToSaveCount = createSelector([getModifiedPathsWaitingToSave], modifiedPaths => modifiedPaths.length);
export const getCampaignAssetObjectType = createSelector([getIsLandingPage, getIsBlogPost, getIsEmail, getIsSitePage], (isLandingPage, isBlogPost, isEmail, isSitePage) => {
  if (isLandingPage) return 'LANDING_PAGE';
  if (isBlogPost) return 'BLOG_POST';
  if (isEmail) return 'MARKETING_EMAIL';
  if (isSitePage) return 'SITE_PAGE';
  return undefined;
});
export const getCampaignCrmIdProperty = createSelector([getIsLandingPage, getIsBlogPost], (isLandingPage, isBlogPost) => isLandingPage || isBlogPost ? 'hs_origin_asset_id' : undefined);